import { createContext, useContext, useEffect, useState } from "react";
import { Division } from "../../models/Division";
import { Magasin } from "../../models/Magasin";
import { Section } from "../../models/Section";
import { useGetDashboardQuery, useGetDivisionsQuery, useGetMagasinsQuery, useGetSectionsQuery } from "../../redux/features/cache";
import { SortDescriptor } from "@progress/kendo-data-query";


export enum ViewMode {
    PRODUCT = "product",
    STORE = "store",
}

export enum AccordionType {
    MAGASIN = "magasin",
    DIVISION = "division",
    SECTION = "section",
}

export interface GridDataItem {
    name: string;
    nombreVise: number;
    nombreReel: number;
}

const emptyMagasins: Magasin[] = [];
const emptyDivisions: Division[] = [];
const emptySections: Section[] = [];
const emptyDashboardData: any = [];

export interface DashboardDataState {
    currentView: ViewMode;
    title: string;
    magasins: Magasin[];
    sections: Section[];
    divisions: Division[];
    selectedMagasin: Magasin | undefined;
    selectedDivision: Division | undefined;
    selectedSection: Section | undefined;
    dashboardData: any;
    gridItems: GridDataItem[];
    expandedAccordions: AccordionType | false;
    gridSort: SortDescriptor[];
}

const initialDashboardDataState: DashboardDataState = {
    currentView: ViewMode.PRODUCT,
    title: "",
    magasins: emptyMagasins,
    sections: emptySections,
    divisions: emptyDivisions,
    selectedMagasin: undefined,
    selectedDivision: undefined,
    selectedSection: undefined,
    dashboardData: emptyDashboardData,
    gridItems: [],
    expandedAccordions: false,
    gridSort: [],
};

interface DashboardDataActions {
    setCurrentView: (view: ViewMode) => void;
    setTitle: (title: string) => void;
    setSelectedMagasin: (magasin: Magasin | undefined) => void;
    setSelectedDivision: (division: Division | undefined) => void;
    setSelectedSection: (section: Section | undefined) => void;
    setExpandedAccordions: (accordion: AccordionType | false) => void;
    setGridSort: (sort: SortDescriptor[]) => void;
}

export const dashboardContext = createContext<DashboardDataState & DashboardDataActions>({
    ...initialDashboardDataState,
    setCurrentView: () => { },
    setTitle: () => { },
    setSelectedMagasin: () => { },
    setSelectedDivision: () => { },
    setSelectedSection: () => { },
    setExpandedAccordions: () => { },
    setGridSort: () => { },
});

export const useDashboardContext = () => {
    const context = useContext(dashboardContext);
    if (!context) {
        throw new Error("useDashboardContext must be used within a DashboardContextProvider");
    }
    return context;
};

export const DashboardContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentView, setCurrentView] = useState<ViewMode>(ViewMode.PRODUCT);
    const [selectedMagasin, setSelectedMagasin] = useState<Magasin>();
    const [selectedDivision, setSelectedDivision] = useState<Division>();
    const [selectedSection, setSelectedSection] = useState<Section>();
    const [expandedAccordions, setExpandedAccordions] = useState<AccordionType | false>(false);

    const [title, setTitle] = useState("");
    const [gridItems, setGridItems] = useState<GridDataItem[]>([]);
    const [gridSort, setGridSort] = useState<SortDescriptor[]>([]);

    const {
        data: magasins = emptyMagasins,
        isLoading: isLoadingMagasins,
        isFetching: isFetchingMagasins,
    } = useGetMagasinsQuery({}, {
        skip: currentView !== ViewMode.STORE,
    });

    const {
        data: divisions = emptyDivisions,
        isLoading: isLoadingDivisions,
        isFetching: isFetchingDivisions,
    } = useGetDivisionsQuery({
        includeSections: false,
        treeView: true,
        plano: true,
        mag: selectedMagasin?.mag_num.toString() || "",
    }, {
        skip: (currentView === ViewMode.STORE && !selectedMagasin),
    });

    const {
        data: sections = emptySections,
        isLoading: isLoadingSections,
        isFetching: isFetchingSections,
    } = useGetSectionsQuery({
        rech: "",
        simple: true,
        treeView: false,
        user: "",
        mag: selectedMagasin?.mag_num.toString() || "",
        div: selectedDivision?.div_num.toString() || "",
    }, {
        skip: (currentView === ViewMode.STORE && (!selectedMagasin || !selectedDivision)) || (currentView === ViewMode.PRODUCT && !selectedDivision),
    });

    const {
        data: dashboardData = emptyDashboardData,
        isLoading: isLoadingDashboardData,
        isFetching: isFetchingDashboardData,
    } = useGetDashboardQuery({
        section: (currentView === ViewMode.PRODUCT && selectedSection?.plans_seq.toString()) || "",
        div: selectedDivision?.div_num.toString() || "",
        detail: false,
        mag: selectedMagasin?.mag_num.toString() || "",
    }, {
        skip: (currentView === ViewMode.PRODUCT && (!selectedDivision || !selectedSection)) || (currentView === ViewMode.STORE && (!selectedMagasin || !selectedDivision)),
    });

    useEffect(() => {
        if (currentView === ViewMode.PRODUCT) {
            setTitle("Magasins");
            if (!Array.isArray(dashboardData) || dashboardData.length !== 1) {
                setGridItems([]);
                return;
            }
            const result = dashboardData[0].sectionStores?.map((sectionStore: any) => ({
                name: sectionStore.magasin?.mag_nom_aff || "Inconnu",
                nombreVise: sectionStore.nombre_vise,
                nombreReel: sectionStore.nombre_reel,
            })) || [];
            setGridItems(result);
        } else {
            setTitle("Sections");
            if (!Array.isArray(dashboardData)) {
                setGridItems([]);
                return;
            }
            const result = dashboardData?.map((data) => {
                const sectionStore = data.sectionStores?.find((s: any) => s.mag_num === selectedMagasin?.mag_num);
                return {
                    name: data.sectionName,
                    nombreVise: sectionStore ? sectionStore.nombre_vise : 0,
                    nombreReel: sectionStore ? sectionStore.nombre_reel : 0,
                }
            });
            setGridItems(result);
        }
    }, [dashboardData, currentView]);

    useEffect(() => {
        if (currentView === ViewMode.STORE) {
            if (!selectedMagasin) {
                setSelectedDivision(undefined);
                setExpandedAccordions(AccordionType.MAGASIN);
            } else if (!selectedDivision) {
                setExpandedAccordions(AccordionType.DIVISION);
            }

            if (!selectedMagasin || !selectedDivision) {
                setGridItems([]);
            }
        } else {
            if (!selectedDivision) {
                setSelectedSection(undefined);
                setExpandedAccordions(AccordionType.DIVISION);
            } else if (!selectedSection) {
                setExpandedAccordions(AccordionType.SECTION);
            }

            if (!selectedDivision || !selectedSection) {
                setGridItems([]);
            }
        }
    }, [selectedMagasin, selectedDivision, selectedSection, currentView]);

    return (
        <dashboardContext.Provider value={{
            currentView,
            setCurrentView,
            title,
            gridItems,
            expandedAccordions,
            setExpandedAccordions,
            magasins,
            divisions,
            sections,
            selectedMagasin,
            setSelectedMagasin,
            selectedDivision,
            setSelectedDivision,
            selectedSection,
            setSelectedSection,
            dashboardData,
            setTitle,
            gridSort,
            setGridSort,
        }}>
            {children}
        </dashboardContext.Provider>
    );
}